import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import MainGrid from '../../components/Dashboard/MainGrid';
import StatCard from '../../components/Dashboard/StatCard';
import ChartCard from '../../components/Dashboard/ChartCard';
import CombinedChart from '../../components/Dashboard/CombinedChart';
import DailyAverageChart from '../../components/Dashboard/DailyAverageChart';
import HourlyAverageChart from '../../components/Dashboard/HourlyAverageChart';
import FlexibilityChartWeekly from '../../components/Dashboard/FlexibilityChartWeekly';
import { dataAnalyticsTooltips } from '../../config/dashboardConfig';
import { formatEnergyValue, formatMaxEnergyValue } from '../../utils/energyUnits';

const DataAnalyticsPage = ({ analyticsAPIData, chartsAPIData, selectedDashboardOption }) => {

  const [pageSelectedOption, setPageSelectedOption] = useState('');
  useEffect(() => {
    setPageSelectedOption(selectedDashboardOption || 'No Option Selected');
  }, [selectedDashboardOption]);

  // If analyticsAPIData is not yet loaded (handled by parent, but good for safety)
  if (!analyticsAPIData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
        {/* Parent UnifiedDashboard will show the main loader.
              This can be a more subtle placeholder or skeleton if needed,
              or simply return null if parent handles all loading states. */}
        <Typography>Waiting for analytics data...</Typography>
      </Box>
    );
  }

  const data = analyticsAPIData; // For convenience if you don't want to refactor all `data.`

  // Check if we have any data to display
  const hasAnyData = data.hasConsumptionData || data.hasProductionData || data.hasDurationData || data.hasMaxData || data.hasAvgData;

  if (!hasAnyData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No data available for the selected configuration.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, marginLeft: 2, marginRight: 2 }}>
      <MainGrid>
        {/* Row 1: Header Row */}
        <Grid container spacing={2} alignItems="center">
          {/* Empty Cell */}
          <Grid item xs={12} sm={2} md={2}></Grid>

          {/* Total */}
          <Grid item xs={12} sm={3} md={3}>
            <Typography
              variant="h6"
              color="text.primary"
              align="center"
            >
              Total
            </Typography>
          </Grid>

          {/* Average */}
          <Grid item xs={12} sm={3} md={3}>
            <Typography
              variant="h6"
              color="text.primary"
              align="center"
            >
              Average
            </Typography>
          </Grid>

          {/* Max */}
          <Grid item xs={12} sm={3} md={3}>
            <Typography
              variant="h6"
              color="text.primary"
              align="center"
            >
              Max
            </Typography>
          </Grid>
        </Grid>

        {/* Spacer */}
        <Box sx={{ width: '100%', height: 20 }} />

        {/* Row 2: Consumption - Only show if we have consumption data */}
        {data.hasConsumptionData && (
          <Grid container spacing={2} alignItems="flex-start">
            {/* Consumption Label */}
            <Grid item xs={12} sm={2} md={2}>
              <Typography
                variant="h5"
                color="text.primary"
                sx={{ textAlign: 'center', mt: 2 }}
              >
                Consumption
              </Typography>
            </Grid>

            {/* Total Consumption */}
            <Grid item xs={12} sm={3} md={3}>
              <StatCard
                value={{
                  main: data.totalConsumption !== null ? formatEnergyValue(data.totalConsumption) : 'N/A',
                  sub: null,
                }}
                tooltip={dataAnalyticsTooltips.totalConsumption}
                color="primary"
              />
            </Grid>

            {/* Average Consumption */}
            <Grid item xs={12} sm={3} md={3}>
              <StatCard
                value={{
                  main: data.avgConsumption !== null && data.hasAvgConsumptionData ? formatEnergyValue(data.avgConsumption) : 'N/A',
                  sub: null,
                }}
                tooltip={dataAnalyticsTooltips.avgConsumption}
                color="primary"
              />
            </Grid>

            {/* Max Consumption */}
            <Grid item xs={12} sm={3} md={3}>
              <StatCard
                value={{
                  main: data.maxConsumptionDay && data.hasMaxConsumptionData
                    ? formatMaxEnergyValue(data.maxConsumptionDay.total_consumption_kw)
                    : 'N/A',
                  sub: data.maxConsumptionDay && data.hasMaxConsumptionData ? data.maxConsumptionDay.date : null,
                }}
                tooltip={dataAnalyticsTooltips.duration}
                color="primary"
              />
            </Grid>
          </Grid>
        )}

        {/* Spacer - Only show if we had consumption data */}
        {data.hasConsumptionData && <Box sx={{ width: '100%', height: 40 }} />}

        {/* Row 3: Production - Always show production boxes */}
        <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 2 }}>
          {/* Production Label */}
          <Grid item xs={12} sm={2} md={2}>
            <Typography
              variant="h5"
              color="text.primary"
              sx={{ textAlign: 'center', mt: 2 }}
            >
              Production
            </Typography>
          </Grid>

          {/* Total Production */}
          <Grid item xs={12} sm={3} md={3}>
            <StatCard
              value={{
                main: data.totalProduction !== null && data.hasProductionData ? formatEnergyValue(data.totalProduction) : 'N/A',
                sub: null,
              }}
              tooltip={dataAnalyticsTooltips.totalProduction}
              color="secondary"
            />
          </Grid>

          {/* Average Production */}
          <Grid item xs={12} sm={3} md={3}>
            <StatCard
              value={{
                main: data.avgProduction !== null && data.hasAvgProductionData && data.hasProductionData ? formatEnergyValue(data.avgProduction) : 'N/A',
                sub: null,
              }}
              tooltip={dataAnalyticsTooltips.avgProduction}
              color="secondary"
            />
          </Grid>

          {/* Max Production */}
          <Grid item xs={12} sm={3} md={3}>
            <StatCard
              value={{
                main: data.maxProductionDay && data.hasMaxProductionData && data.hasProductionData
                  ? formatMaxEnergyValue(data.maxProductionDay.total_production_kw)
                  : 'N/A',
                sub: data.maxProductionDay && data.hasMaxProductionData && data.hasProductionData ? data.maxProductionDay.date : null,
              }}
              tooltip={dataAnalyticsTooltips.duration}
              color="secondary"
            />
          </Grid>
        </Grid>

        {/* Spacer */}
        <Box sx={{ width: '100%', height: 40 }} />

        {/* Charts Section */}
        <Grid container spacing={2}>
          {/* Combined Chart - Only show if we have chart data */}
          {chartsAPIData?.hasCombinedChartData && (
            <Grid item xs={12} md={9}>
              <ChartCard
                title="Combined Consumption and Production Data"
                tooltip={dataAnalyticsTooltips.combined}
              >
                <CombinedChart chartData={chartsAPIData?.combinedChartData} />
              </ChartCard>
            </Grid>
          )}

          {/* Show message if chart data is not available */}
          {!chartsAPIData?.hasCombinedChartData && (
            <Grid item xs={12} md={9}>
              <ChartCard
                title="Combined Consumption and Production Data"
                tooltip={dataAnalyticsTooltips.combined}
              >
                <Box sx={{
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="h6">
                    Chart data is not available
                  </Typography>
                </Box>
              </ChartCard>
            </Grid>
          )}

          {/* Duration and Frequency StatCards */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <StatCard
                title="Duration"
                value={{
                  main: data.duration !== null && data.hasDurationData ? `${Math.floor(data.duration)} days` : 'N/A',
                  sub: null,
                }}
                tooltip={dataAnalyticsTooltips.duration}
                color="grey"
              />
              <StatCard
                title="Frequency"
                value={{
                  main: data.frequency || 'N/A',
                  sub: null,
                }}
                tooltip={dataAnalyticsTooltips.frequency}
                color="grey"
              />
            </Box>
          </Grid>
        </Grid>

        {/* Spacer */}
        <Box sx={{ width: '100%', height: 40 }} />

        {/* Additional Charts - Only show if we have chart data */}
        {chartsAPIData?.hasCombinedChartData && (
          <Grid container spacing={2}>
            {/* Daily Average Load Profile */}
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Daily Average Load Profile"
                tooltip={dataAnalyticsTooltips.dailyAverage}
              >
                <DailyAverageChart chartData={chartsAPIData?.combinedChartData} />
              </ChartCard>
            </Grid>

            {/* Hourly Average Load Profile */}
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Hourly Average Load Profile"
                tooltip={dataAnalyticsTooltips.hourlyAverage}
              >
                <HourlyAverageChart chartData={chartsAPIData?.combinedChartData} />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {/* Weekly Flexibility Data */}
        {selectedDashboardOption === 'Aggregated' && chartsAPIData?.hasFlexibilityData && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <ChartCard
                title="Weekly Flexibility Data"
                tooltip={dataAnalyticsTooltips.weeklyFlexibility}
              >
                <FlexibilityChartWeekly chartData={chartsAPIData?.flexibilityData} />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {/* Show message if flexibility data is not available for Aggregated method */}
        {selectedDashboardOption === 'Aggregated' && !chartsAPIData?.hasFlexibilityData && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Box sx={{
                p: 2,
                backgroundColor: 'warning.light',
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="body1" color="warning.contrastText">
                  Weekly flexibility data is not available for this dataset
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </MainGrid>
    </Box>
  );
};

export default DataAnalyticsPage;
